import { Fill, Stroke, Style, Text } from 'ol/style';
import { WKT } from 'ol/format';
import { GeometryCollection, LineString } from 'ol/geom';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { LINE_STRING } from 'client/utils/constants/geometryType';
import {
    ANNOTATIONS_ASSET_TYPE,
    DIMENSIONS_ASSET_TYPE,
    DIMENSION_DESCRIPTION_TYPE,
    NI_ANNOTATIONS_ASSET_TYPE,
    NI_DIMENSIONS_ASSET_TYPE
} from 'client/pages/MapPage/layers/constants';

// This is a custom property we add to each geometry in dimension's GeometryCollection
// so that we can style each part of the dimension separately, and know which is which.
// Doesn't matter what it's called (merlin the happy pig, perchance?)
const dimensionTypeProperty = '_dimension_type';

const deg2rad = (degrees) => {
    return degrees * (Math.PI / 180);
};

const rad2deg = (radians) => {
    return Math.round(radians * (180 / Math.PI));
};

const roundToTwo = (num) => {
    return Math.floor((num + Number.EPSILON) * 100) / 100;
};

const radiansFromCoords = ([[x1, y1], [x2, y2]]) => {
    const dy = y2 - y1;
    const dx = x2 - x1;
    return Math.atan2(-dy, -dx);
};

const fontStringFromFeatureValues = (values) => {
    // Format: https://developer.mozilla.org/en-US/docs/Web/CSS/font
    const fontParts = [];
    if (values.bold) {
        fontParts.push('bold');
    }
    if (values.italic) {
        fontParts.push('italic');
    }
    if (values.fontsize) {
        let fontsize = values.fontsize;
        if (!(fontsize + '').includes('px')) { // annotations do, dimensions don't
            fontsize += 'px';
        }
        fontParts.push(fontsize);
    } else {
        fontParts.push('10px');
    }
    if (values.fontname) {
        fontParts.push(`'${values.fontname}'`);
    } else {
        fontParts.push('sans-serif');
    }
    return fontParts.join(' ');
};

const shouldDeriveTextAngleFromLine = (feature) => {
    const properties = feature.getProperties();
    const geometryType = properties.geometry?.getType();
    return isDimension(feature) || (properties.associatedtext && geometryType === LINE_STRING);
};

const getTextOrientationFromFeature = (feature) => {
    const properties = feature.getProperties();
    const featureIsDimension = isDimension(feature);

    // Account for rotation unit
    let orientation = 0 - properties.orientation || 0;
    if (properties.orientationmeasurementunits?.toLocaleLowerCase() !== 'radians') {
        orientation = deg2rad(orientation);
    }

    // Some annotations represent the angle of text as a LineString.
    // So we should derive the text orientation from the angle of the line.
    // TODO: it would be great if we could encode this logic at the ingestion stage
    // and then the FE would be able to just read .orientation and job done.
    if (shouldDeriveTextAngleFromLine(feature)) {
        // Align text to the geometry
        let lineToAlignTextWith = properties.geometry;

        if (featureIsDimension && lineToAlignTextWith) {
            // Unless it's a dimension, in which case find the dimensionline among the geometries, and use that instead
            lineToAlignTextWith = lineToAlignTextWith.getGeometries().find(geometry => geometry.get(dimensionTypeProperty) === 'dimensionline');
        }

        if (!lineToAlignTextWith) {
            // If we can't find a line to align the text with, just return the orientation we were given
            return orientation;
        }

        // If the line was drawn right-to-left, it produces a rotation that draws the text upsidedown.
        // So calculate the orientation from a line that is left to right.
        const leftToRightLine = lineToAlignTextWith.getCoordinates().slice(0, 2).sort(([x1], [x2]) => {
            // Note: this doesn't actually redraw the line itself because sometimes the lefty-rightyness is important
            // e.g. lines with arrows at the 'end' - we don't want to flip the arrow, but we do want to flip the text.
            // TODO: This should have tests
            if (x1 === x2) {
                return 0;
            }
            return x1 > x2 ? -1 : 1;
        });
        orientation = 0 - radiansFromCoords(leftToRightLine);
    }

    return orientation;
};

const arrowAtLineTerminal = (line, strokeStyle, arrowPointsToStart = false) => {
    const start = arrowPointsToStart ? line.getFirstCoordinate() : line.getLastCoordinate();
    const end = arrowPointsToStart ? line.getLastCoordinate() : line.getFirstCoordinate();

    const dx = end[0] - start[0];
    const dy = end[1] - start[1];
    const angle = Math.atan2(dy, dx);

    // Adjust arrow line length so that it isn't comparitively huge for very short lines
    const lineLength = Math.sqrt(Math.pow(end[0] - start[0], 2) + Math.pow(end[1] - start[1], 2));
    const length = Math.min(0.5, Math.max(0.3, lineLength / 3));

    const arrowAngle = 0.55; // I *think* this is in radians? Dammit Jim I'm a coder not a mathematician.
    const arrowPart1 = [[start[0], start[1]], [start[0] + length * Math.cos(angle + arrowAngle), start[1] + length * Math.sin(angle + arrowAngle)]];
    const arrowPart2 = [[start[0], start[1]], [start[0] + length * Math.cos(angle - arrowAngle), start[1] + length * Math.sin(angle - arrowAngle)]];

    return [
        new Style({
            geometry: new LineString(arrowPart1),
            stroke: strokeStyle
        }),
        new Style({
            geometry: new LineString(arrowPart2),
            stroke: strokeStyle
        })
    ];
};

const arrowAtStartOf = (line, stroke) => {
    return arrowAtLineTerminal(line, stroke, false);
};
const arrowAtEndOf = (line, stroke) => {
    return arrowAtLineTerminal(line, stroke, true);
};

const generateDimensionStyles = (featureProperties, textStyle, stroke) => {
    const styles = [];
    // This is a dimension, we need extra styling
    // e.g. adding arrows, only drawing text in certain parts of the geometry etc

    // Extract the parts of the dimension that we set up in normaliseDimensionFeature
    const geometries = featureProperties.geometry.getGeometries();

    const geometryByType = {};
    geometries.forEach(geometry => {
        const dimensionType = geometry.get(dimensionTypeProperty);
        geometryByType[dimensionType] = geometry;
    });
    const hasExtensionLines = geometryByType.startextensionline || geometryByType.endextensionline;
    const hasDimensionExtensions = geometryByType.startdimensionlineextension || geometryByType.enddimensionlineextension;

    // Draw the extension lines, they don't get arrows
    if (hasExtensionLines) {
        if (geometryByType.startextensionline) {
            styles.push(new Style({
                geometry: geometryByType.startextensionline,
                stroke
            }));
        }
        if (geometryByType.endextensionline) {
            styles.push(new Style({
                geometry: geometryByType.endextensionline,
                stroke
            }));
        }
    }

    if (hasDimensionExtensions) {
        // Dimension line extensions do get arrows
        if (geometryByType.startdimensionlineextension) {
            styles.push.apply(styles, arrowAtStartOf(geometryByType.startdimensionlineextension, stroke));
            styles.push(new Style({
                geometry: geometryByType.startdimensionlineextension,
                stroke
            }));
        }
        if (geometryByType.enddimensionlineextension) {
            styles.push.apply(styles, arrowAtStartOf(geometryByType.enddimensionlineextension, stroke));
            styles.push(new Style({
                geometry: geometryByType.enddimensionlineextension,
                stroke
            }));
        }
    } else if (geometryByType.dimensionline) {
        // No dimension line extensions - add arrows to each end of dimension line
        styles.push.apply(styles, arrowAtStartOf(geometryByType.dimensionline, stroke));
        styles.push.apply(styles, arrowAtEndOf(geometryByType.dimensionline, stroke));
    }

    // Finally: draw the dimension line itself, with the text label attached
    if (geometryByType.dimensionline) {
        styles.push(new Style({
            geometry: geometryByType.dimensionline,
            stroke,
            text: textStyle
        }));
    }

    return styles;
};

const styleFunctionWithScale = (feature, displayScale = 1000) => {
    // Takes a feature and returns an array of OL styles that govern how it should be drawn

    const styles = [];
    const properties = feature.getProperties();
    const referenceScale = properties.referencescale || 1000;
    const featureColor = properties.textcolour || '#000000';
    const strokeWidth = 0.25 * (referenceScale / displayScale);
    const featureIsDimension = isDimension(feature);

    // Show geometry unless explicitly set to false (if undefined: show)
    let showGeometry = !(properties.geometryvisible === false);
    if (!featureIsDimension && shouldDeriveTextAngleFromLine(feature)) {
        // But if the line is only there to convey the angle, hide unless explicitly set to true
        showGeometry = properties.geometryvisible === true;
    }

    let textStyle = null;
    if (properties.associatedtext) {
        // Adjust pixel sizes for referencescale, using agreed default values
        const fontsize = roundToTwo((properties.fontsize || 10) * (referenceScale / displayScale));

        // Hitch text up off the dimensionline unless we've been given an explicit textplacement
        const shouldHitchTextUp = featureIsDimension && !properties.textplacementy;
        const textOffsetAmount = (-5 * (referenceScale / displayScale));
        const orientation = getTextOrientationFromFeature(feature);

        textStyle = new Text({
            overflow: true,
            placement: 'point',
            font: fontStringFromFeatureValues({
                ...properties,
                fontsize
            }),
            text: properties.associatedtext,
            rotation: orientation,
            fill: new Fill({
                color: featureColor
            }),
            stroke: null,
            offsetX: 0, // note: these offsets are pixel value offsets from the geometry, rather than lon/lat values like textplacementx and y
            offsetY: shouldHitchTextUp ? textOffsetAmount : 0
        });
    }

    const stroke = showGeometry
        ? new Stroke({
            color: featureColor,
            width: strokeWidth
        })
        : null;

    if (featureIsDimension) {
        return generateDimensionStyles(properties, textStyle, stroke);
    } // else is annotation and we continue:

    const annotationStyle = new Style({
        stroke
    });

    if (properties.textplacementy && properties.textplacementx) {
        // Add the text style separately to the annotation's geometry
        const placedTextStyle = new Style({
            geometry: new Point([properties.textplacementx, properties.textplacementy]),
            stroke,
            text: textStyle
        });
        styles.push(placedTextStyle);
    } else {
        // No specific text placement so just add it to the annotation style and let OL place it
        annotationStyle.setText(textStyle);
    }

    styles.push(annotationStyle);
    return styles;
};

const normaliseDimensionFeature = (feature) => {
    if (!isDimension(feature)) {
        // only dimensions need extra processing.
        return feature;
    }
    const values = feature.getProperties();

    const format = new WKT();
    const dimension = new Feature();
    // Dimension features *do* come with a geometryCollection, but they *also* come with .dimensionline etc as WKT strings
    // Having discussed this with 1spatial, the properties are more explicit and reliable
    // so let's obliterate the supplied geometry and use the named properties instead.
    // This also means questions like 'which of the three LineStrings in the geometry is the dimension line?' will no longer come up
    delete values.geometry;

    // Reconstruct a geometry collection from the properties, preserving the names so we can use them in the style function
    // - otherwise it's just a GeometryCollection with 2 lines and 4 points, and we don't know which is the dimension line etc
    const geometries = [];
    dimensionProperties.forEach(key => {
        if (values[key]) {
            try {
                const geometry = format.readFeature(values[key]).getGeometry();
                geometry.set(dimensionTypeProperty, key);
                geometries.push(geometry);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(`Failed to parse ${key} as WKT:`, values[key]);
            }
        }
    });
    dimension.setProperties(values);
    dimension.setGeometry(new GeometryCollection(geometries));

    // Add the dimension label:
    if (!values.associatedtext) {
        // Generate text from dimension values - see #7346
        const length = roundToTwo(values.dimensionvalue_length);

        // Convert units on FE as per NB instruction
        const prettyUnits = {
            millimeter: 'mm',
            millimeters: 'mm',
            centimeter: 'cm',
            centimeters: 'cm',
            meter: 'm',
            meters: 'm',
            metre: 'm',
            metres: 'm',
            foot: 'ft',
            feet: 'ft',
            inch: 'in',
            inches: 'in',
            lightyear: ' lightyear',
            lightyears: ' lightyears'
        };
        const longUnit = values.dimensionvalue_unitofmeasure || '';
        const units = prettyUnits[longUnit.trim().toLocaleLowerCase()] || ` ${longUnit}`;
        dimension.set('associatedtext', `${length}${units}`);
    }

    // Make sure the line gets displayed
    dimension.set('geometryvisible', true);
    return dimension;
};

const isAnnotationOrDimension = (feature) => {
    return isAnnotation(feature) || isDimension(feature);
};

const isAnnotation = (feature) => {
    return feature?.values_?.assetType === ANNOTATIONS_ASSET_TYPE || feature?.values_?.assetType === NI_ANNOTATIONS_ASSET_TYPE;
};

const dimensionProperties = [
    'dimensionline', //                  }-- dimension line - there is also startdimensionpoint and enddimensionpoint, but we actually derive those from the line.
    'startextensionline', //             \__ extension lines
    'endextensionline', //               /
    'startdimensionlineextension', //    \__ dimension line extensions
    'enddimensionlineextension' //       /
];

const isDimension = (feature) => {
    if (feature?.values_?.assetType === DIMENSIONS_ASSET_TYPE || feature?.values_?.assetType === NI_DIMENSIONS_ASSET_TYPE) {
        return true;
    }
    const values = feature?.getProperties?.();
    return Boolean(
        values?.descriptiontype?.toUpperCase() === DIMENSION_DESCRIPTION_TYPE || // this should probably be enough but
        dimensionProperties.some(key => values?.[key]?.length) || // if it quacks like a dimension, let's treat it as one
        values?.dimensionvalue_length
    );
};

const styleFunction = (feature, clippingGeometry, mapScale) => {
    if (clippingGeometry) {
        const featureExtent = feature.getGeometry().getExtent();
        const extentPoints = [[featureExtent[0], featureExtent[1]], featureExtent[2], featureExtent[3]];
        const outsideAOI = extentPoints.every(point => !clippingGeometry.containsXY(point[0], point[1]));
        if (outsideAOI) {
            return [];
        }
    }
    return styleFunctionWithScale(feature, mapScale);
};

const getParams = (bbox, projectionCode, typeName, ownerId) => {
    return {
        service: 'WFS',
        version: '1.1.0',
        request: 'GetFeature',
        typeName,
        outputFormat: 'application/json',
        srsName: projectionCode,
        viewParams: `assetowner_id:${ownerId}`,
        bbox: `${bbox.join(',')},${projectionCode}`
    };
};

export {
    getParams,
    isAnnotationOrDimension,
    isAnnotation,
    isDimension,
    styleFunction,
    radiansFromCoords,
    fontStringFromFeatureValues,
    styleFunctionWithScale,
    normaliseDimensionFeature,
    arrowAtLineTerminal,
    generateDimensionStyles,
    deg2rad,
    rad2deg
};
